<template>
  <div>
    <!-- fixed is set to avoid issues where cells change size when sorting/filtering/pagination takes place -->
    <!-- if you want to implement a non-fixed table, make sure to account for those potential issues -->
    <!-- see: https://linear.app/userevidence/issue/UE-1351/use-fixed-layout-for-uetable -->
    <div class="rounded border overflow-hidden">
      <b-table
        :class="classObject"
        v-bind="$attrs"
        v-on="$listeners"
        :head-variant="variant"
        empty-text="Nothing to show"
        sort-icon-left
        show-empty
        fixed
        :per-page="perPage"
        :current-page="currentPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        ref="ueTable"
        v-model="displayedItems"
      >
        <!-- Allow customization of column widths -->
        <!-- Approach taken from: https://bootstrap-vue.org/docs/components/table#table-colgroup -->
        <template #table-colgroup="scope">
          <col
            v-for="field in scope.fields"
            :key="field.key"
            :style="{ width: columnWidths[field.key] }"
            :data-cy="field.key + '-column'"
          />
        </template>

        <!-- b-table uses scoped named slots to allow for custom rendering of cells. -->
        <!-- This business here passes through all scoped slots from parent. -->
        <!-- So for instance you can do this in parent: <UeTable><template #cell(name)="data"></template></UeTable> -->
        <!-- see: https://bootstrap-vue.org/docs/components/table#scoped-field-slots -->
        <!--      https://stackoverflow.com/questions/53430731/vuejs-nested-slots-how-to-pass-slot-to-grandchild -->
        <template v-for="(_, slot) in $scopedSlots" v-slot:[slot]="props">
          <slot :name="slot" v-bind="props" />
        </template>
      </b-table>
    </div>
    <div class="mt-3" v-if="totalRows > 0 && !hidePaginationControls">
      <b-pagination
        v-model="currentPage"
        :total-rows="totalRows"
        :per-page="perPage"
        :limit="paginationLimit"
        :hide-goto-end-buttons="paginationHideGotoEndButtons"
      />
      <small>Displaying {{ startOfCurrentPage }}-{{ endOfCurrentPage }} of {{ totalRows }}</small>
    </div>
  </div>
</template>

<script lang="ts">
import { mapState, mapWritableState } from 'pinia'
import { usePaginationStore } from '@app/stores/tables/PaginationStore'

export default {
  inheritAttrs: false,
  data() {
    return {
      displayedItems: [],
    }
  },
  props: {
    // Supply custom widths for specific columns. An object where keys are column names
    // (as defined in the table `items` prop) and values are valid CSS widths.
    // Example: {name: '10%'}
    columnWidths: {
      type: Object,
      required: false,
      default: () => {
        return {}
      },
    },
    useDefaultStyle: {
      type: Boolean,
      required: false,
      default: false,
    },
    paginationLimit: {
      type: Number,
      required: false,
      default: 5, // Default BootstrapVue pagination limit
    },
    paginationHideGotoEndButtons: {
      type: Boolean,
      required: false,
      default: false,
    },
    hidePaginationControls: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    // There are cases where we want the default Bootstrap table,
    // without the customizations of the Results UI.
    // Ideally, the default should the plain Bootstrap table, and the customizations
    // should be opt-in. However, we have quite a few tables that are already using the default,
    // so we're doing the other way around for now.
    // TODO: Discuss this and maybe change the current usages.
    classObject() {
      return {
        'ue-table': !this.useDefaultStyle,
        'ue-table-empty': this.displayedItems.length === 0,
      }
    },
    variant() {
      // 'light' is the other variant, but it isn't the default Bootstrap variant so we return an empty string.
      return this.useDefaultStyle ? '' : 'dark'
    },
    ...mapState(usePaginationStore, ['startOfCurrentPage', 'endOfCurrentPage', 'totalRows']),
    ...mapWritableState(usePaginationStore, ['currentPage', 'perPage', 'sortBy', 'sortDesc']),
  },
  methods: {
    // Exposes b-table's `refresh` to parent
    refresh() {
      this.$refs.ueTable.refresh()
    },
  },
}
</script>

<style lang="sass" scoped>
::v-deep
  .table
    margin-bottom: 0 !important // this important is needed to override the default b-table margin-bottom of 1rem
  table.ue-table-empty
    thead
      display: none !important
    td
      border: none !important

  table.ue-table
    tbody
      td
        font-size: var(--font-size-sm)
      tr:hover:not(.b-table-details)
        cursor: pointer
      tr.b-table-details
        td
          padding: 0 !important
      tr.b-table-details:hover
        background-color: transparent // if you want a hover effect, do it on the slot content

  table.b-table[aria-busy='true']
    // Overrides the 0.55 for when table is busy
    // as this usually just causes it to flicker.
    opacity: 1

  thead
    th
      border-top: none // Fixes a Firefox glitch where the border appears despite border-top-left-radius on the parent table
      border-bottom: none!important // overriding a Bootstrap !important
      white-space: nowrap
</style>
